import SyncableModel from "../../database/SyncableModel"
import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { Sampling } from "../../database"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { withBaseModel } from "../extensions/with-base-model"
import { BaseModelStoreProps } from "../base-model-store/base-model-store"
import JalaAnalytic from "../../utils/jala-analytic"

const SamplingStoreProps = {
  ...BaseModelStoreProps
}

/**
 * Model description here for TypeScript hints.
 */
export const SamplingStoreModel = types
  .model("SamplingStore")
  .props(SamplingStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .extend(withBaseModel)
  .views((self) => ({})) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof SamplingStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    init() {
      self.initModel();
    },
    async store(data) {
      if (self.rootStore.appStore?.isOffline)
        return this.storeOffline(data)
      return self.storeModel(Sampling, data, true, async (success, model: SyncableModel) => {
        const { fileStore } = self.rootStore
        for (let i = 0; i < (data.files || []).length; i++) {
          if (!data.files[i]) continue;
          await fileStore.store({ fileable_id: model.getKey(), ...data.files[i] })
        }
        return success
      }).then((success: boolean) => {
        const { files, ...restData } = data
        if (success) JalaAnalytic.log('Mobile App: Sampling added', restData);
        return success;
      });
    },
    async storeOffline(data) {
      return self.storeModelOffline(Sampling, data, async (success, model: SyncableModel) => {
        const { fileStore } = self.rootStore
        for (let i = 0; i < (data.files || []).length; i++) {
          if (!data.files[i]) continue;
          await fileStore.storeOffline({ fileable_id: model.getKey(), ...data.files[i] })
        }
        return success
      }).then((success: boolean) => {
        const { files, ...restData } = data
        if (success) JalaAnalytic.log('Mobile App: Sampling added', restData);
        return success;
      });
    },
    async update(model: SyncableModel, data) {
      if (self.rootStore.appStore?.isOffline)
        return this.updateOffline(model, data)
      return self.updateModel(Sampling, model, data, true, async (success, model) => {
        const { fileStore } = self.rootStore
        for (let i = 0; i < (data.files || []).length; i++) {
          if (!data.files[i]) continue;
          const file = model.getRelation('files')
            .find((f) => f.getKey() === data.files[i].id)
          if (file) {
            if (data.files[i]._action === 'delete')
              await fileStore.delete(file)
            else await fileStore.update(file, { fileable_id: model.getKey(), ...data.files[i] })
          } else await fileStore.store({ fileable_id: model.getKey(), ...data.files[i] })
        }
        return success
      }).then((success: boolean) => {
        const { files, ...restData } = data
        if (success) JalaAnalytic.log('Mobile App: Sampling edited', restData);
        return success;
      });
    },
    async updateOffline(model: SyncableModel, data) {
      return self.updateModelOffline(Sampling, model, data, async (success, model) => {
        const { fileStore } = self.rootStore
        for (let i = 0; i < (data.files || []).length; i++) {
          if (!data.files[i]) continue;
          const file = model.getRelation('files')
            .find((f) => f.getKey() === data.files[i].id)
          if (file) {
            if (data.files[i]._action === 'delete')
              await fileStore.deleteOffline(file)
            else await fileStore.updateOffline(file, { fileable_id: model.getKey(), ...data.files[i] })
          } else await fileStore.storeOffline({ fileable_id: model.getKey(), ...data.files[i] })
        }
        return success
      }).then((success: boolean) => {
        const { files, ...restData } = data
        if (success) JalaAnalytic.log('Mobile App: Sampling edited', restData);
        return success;
      });
    },
    async delete(model: SyncableModel) {
      if (self.rootStore.appStore?.isOffline)
        return this.deleteOffline(model)
      return self.deleteModel(Sampling, model, true, async (success) => {
        const { fileStore } = self.rootStore
        const files = model.getRelation('files')
        for (let i = 0; i < files.length; i++) {
          await fileStore.delete(files[i])
        }
        return true;
      }).then((success: boolean) => {
        if (success) JalaAnalytic.log('Mobile App: Sampling deleted', { id: model.getKey() });
        return success;
      });
    },
    async deleteOffline(model: SyncableModel) {
      return self.deleteModelOffline(Sampling, model, async (success) => {
        const { fileStore } = self.rootStore
        const files = model.getRelation('files')
        for (let i = 0; i < files.length; i++) {
          await fileStore.deleteOffline(files[i])
        }
        return true;
      }).then((success: boolean) => {
        if (success) JalaAnalytic.log('Mobile App: Sampling deleted', { id: model.getKey() });
        return success;
      });
    }
  }))

type SamplingStoreType = Instance<typeof SamplingStoreModel>
export interface SamplingStore extends SamplingStoreType {}
type SamplingStoreSnapshotType = SnapshotOut<typeof SamplingStoreModel>
export interface SamplingStoreSnapshot extends SamplingStoreSnapshotType {}
export const createSamplingStoreDefaultModel = () => types.optional(SamplingStoreModel, {})
